import { ConnectProto } from "@racoon/api";
import { isBlank, sortJobsByStep } from "@racoon/lib";
import { isShiftTerminal } from "~/shared/lib/shift-status";
import { compareIds } from "~/shared/lib/compare-ids";
import { isFinalJobStatus } from "~/shared/lib/job-status";

/**
 * The decision model behind "Аварийное завершение смены": which routes and jobs
 * the completion settles, what each becomes, and the existing RPCs that do it.
 * The calls go through an injected API, so the modal only renders and the call
 * sequence is asserted here.
 */

const { JobStatus, RouteStatus, DriverIncidentType } = ConnectProto;

/** What becomes of one unfinished job: back to the pool, counted as done, or dropped. */
export const JOB_RESOLUTIONS = ["unassign", "complete", "cancel"] as const;
export type JobResolution = (typeof JOB_RESOLUTIONS)[number];

export type DistributionMode = "manual" | "transfer";

export type Decision = {
  reason: ConnectProto.DriverIncidentType | undefined;
  /** Free text; "Другое" cannot go without it. */
  explanation: string;
  mode: DistributionMode;
  /** The shift that takes every unfinished route and job; what lands on it is transferPlan's call. */
  target: bigint | null;
};

/** Per job; a job absent here keeps the default, unassign. */
export type Resolutions = ReadonlyMap<bigint, JobResolution>;

/** A job never dispatched cannot be counted as done: CREATED has no edge to FORCE_COMPLETED. */
export const resolutionsFor = (status: ConnectProto.JobStatus): readonly JobResolution[] =>
  status === JobStatus.CREATED ? ["unassign", "cancel"] : JOB_RESOLUTIONS;

const after = <T>(cycle: readonly T[], current: T): T =>
  cycle[(cycle.indexOf(current) + 1) % cycle.length];

/** The order a click walks the resolution pill through, as the route-level modal does. */
export const nextResolution = (
  current: JobResolution,
  status: ConnectProto.JobStatus,
): JobResolution => after(resolutionsFor(status), current);

export const resolutionOf = (resolutions: Resolutions, jobId: bigint): JobResolution =>
  resolutions.get(jobId) ?? "unassign";

const isRouteFinal = (status: ConnectProto.RouteStatus) =>
  status === RouteStatus.COMPLETED ||
  status === RouteStatus.CANCELLED ||
  status === RouteStatus.FORCE_COMPLETED;

/** What becomes of one unfinished route: closed as done, or withdrawn. */
export type RouteResolution = "complete" | "cancel";

/** Per route; a route absent here keeps its usual outcome, the first of routeResolutionsFor. */
export type RouteResolutions = ReadonlyMap<bigint, RouteResolution>;

/**
 * What the backend lets a route become, the usual outcome first: one the driver
 * started is closed as done, one they never started is withdrawn. A returning one
 * is past withdrawing; an unapproved one was never work to count.
 */
export const routeResolutionsFor = (status: ConnectProto.RouteStatus): RouteResolution[] => {
  switch (status) {
    case RouteStatus.IN_PROGRESS:
      return ["complete", "cancel"];
    case RouteStatus.RETURNING:
      return ["complete"];
    case RouteStatus.CREATED:
      return ["cancel", "complete"];
    default:
      return ["cancel"];
  }
};

export const nextRouteResolution = (
  current: RouteResolution,
  status: ConnectProto.RouteStatus,
): RouteResolution => after(routeResolutionsFor(status), current);

/** The stored choice while the route still allows it: a refetch can have moved the route on. */
export const routeResolutionOf = (
  resolutions: RouteResolutions,
  route: ConnectProto.Route,
): RouteResolution => {
  const allowed = routeResolutionsFor(route.status);
  const stored = resolutions.get(route.id);
  return stored && allowed.includes(stored) ? stored : allowed[0];
};

export const routeOutcome = (resolution: RouteResolution): ConnectProto.RouteStatus =>
  resolution === "complete" ? RouteStatus.FORCE_COMPLETED : RouteStatus.CANCELLED;

export const jobOutcome = (resolution: JobResolution): ConnectProto.JobStatus => {
  switch (resolution) {
    case "complete":
      return JobStatus.FORCE_COMPLETED;
    case "cancel":
      return JobStatus.CANCELLED;
    default:
      return JobStatus.CREATED;
  }
};

/** Trip order. */
export const byId = (a: { id: bigint }, b: { id: bigint }) => compareIds(a.id, b.id);

/** The routes the completion has to settle, in trip order. Final ones are left alone. */
export const unfinishedRoutes = (routes: readonly ConnectProto.Route[]): ConnectProto.Route[] =>
  routes.filter((route) => !isRouteFinal(route.status)).sort(byId);

const unfinishedJobs = (route: ConnectProto.Route): ConnectProto.Job[] =>
  sortJobsByStep(route.jobs ?? []).filter((job) => !isFinalJobStatus(job.status));

/** Every job a transfer moves: the unfinished ones of every unfinished route. */
export const transferableJobs = (routes: readonly ConnectProto.Route[]): ConnectProto.Job[] =>
  unfinishedRoutes(routes).flatMap(unfinishedJobs);

const isRouteStarted = (status: ConnectProto.RouteStatus) =>
  status === RouteStatus.IN_PROGRESS || status === RouteStatus.RETURNING;

/**
 * What the transfer does with one unfinished route of the shift. "move" takes the
 * route itself to the target shift, jobs and order included; "create" gives the
 * listed jobs a new trip there, modelled on the route they leave. `jobs` is what
 * travels, so the preview counts and measures it.
 */
export type TransferItem = {
  kind: "move" | "create";
  route: ConnectProto.Route;
  jobs: ConnectProto.Job[];
};

/**
 * The transfer, route by route in trip order. The modal previews exactly this and
 * transferSteps runs exactly this, so the dispatcher sees what will happen.
 */
export const transferPlan = (routes: readonly ConnectProto.Route[]): TransferItem[] =>
  unfinishedRoutes(routes).flatMap((route): TransferItem[] => {
    if (!isRouteStarted(route.status)) {
      return [{ kind: "move", route, jobs: route.jobs ?? [] }];
    }
    // A started route with nothing open has nothing to give; the leftover close settles it.
    const jobs = unfinishedJobs(route);
    return jobs.length > 0 ? [{ kind: "create", route, jobs }] : [];
  });

/** Why the decision cannot be confirmed yet, or null once it can. */
export const confirmBlocker = (decision: Decision): "reason" | "explanation" | "target" | null => {
  if (decision.reason == null || decision.reason === DriverIncidentType.UNDEFINED) return "reason";
  if (decision.reason === DriverIncidentType.OTHER && isBlank(decision.explanation)) {
    return "explanation";
  }
  if (decision.mode === "transfer" && decision.target == null) return "target";
  return null;
};

/** A shift that may take the jobs: not the one being ended, and not closed. */
export const canReceiveTransfer = (shift: ConnectProto.DriverOnShift, sourceShiftId: bigint) =>
  BigInt(shift.id) !== BigInt(sourceShiftId) && !isShiftTerminal(shift.status);

/** The existing RPCs the completion is made of; useForceCompleteRun wires them up. */
export type ForceCompleteApi = {
  cancelJob: (job: ConnectProto.Job) => Promise<unknown>;
  forceCompleteJob: (job: ConnectProto.Job) => Promise<unknown>;
  detachJob: (job: ConnectProto.Job) => Promise<unknown>;
  forceCompleteRoute: (routeId: bigint, complete: bigint[], unassign: bigint[]) => Promise<unknown>;
  cancelRoute: (routeId: bigint, cancel: bigint[], unassign: bigint[]) => Promise<unknown>;
  /** The whole route onto another shift, jobs and order included. */
  moveRoute: (routeId: bigint, driverOnShiftId: bigint) => Promise<unknown>;
  /**
   * A new trip on the shift with the pooled jobs, in that order, departing and
   * unloading where `like` does. One call: the trip never exists without its jobs.
   */
  createTrip: (
    like: ConnectProto.Route,
    driverOnShiftId: bigint,
    jobIds: bigint[],
  ) => Promise<unknown>;
  /** The shift's routes as the backend has them now. */
  loadRoutes: () => Promise<ConnectProto.Route[]>;
  completeShift: (reason: ConnectProto.DriverIncidentType, explanation: string) => Promise<unknown>;
};

/**
 * Outlives a failed attempt, so the retry still gives the jobs it detached their
 * trip: those sit in the pool, on no route the retry reads. Per route they left,
 * which the new trip is modelled on.
 */
export type RunMemory = {
  detached?: { like: ConnectProto.Route; jobIds: bigint[] }[];
};

type Step = () => Promise<unknown>;

/**
 * The calls that close one route. ForceCompleteRoute carries the completed and
 * pooled jobs, CancelRoute the cancelled and pooled ones; the third resolution
 * goes job by job first. A job settled that way can make the backend move a
 * started route on to RETURNING: closing it as done still goes through, while
 * withdrawing it is refused, and the retry then offers only to close it.
 */
const routeSteps = (
  api: ForceCompleteApi,
  route: ConnectProto.Route,
  resolve: (job: ConnectProto.Job) => JobResolution,
  outcome: RouteResolution,
): Step[] => {
  const jobs = unfinishedJobs(route);
  const resolved = (resolution: JobResolution) => jobs.filter((job) => resolve(job) === resolution);
  const ids = (resolution: JobResolution) => resolved(resolution).map((job) => job.id);

  if (outcome === "complete") {
    return [
      ...resolved("cancel").map((job) => () => api.cancelJob(job)),
      () => api.forceCompleteRoute(route.id, ids("complete"), ids("unassign")),
    ];
  }
  return [
    ...resolved("complete").map((job) => () => api.forceCompleteJob(job)),
    () => api.cancelRoute(route.id, ids("cancel"), ids("unassign")),
  ];
};

/**
 * Carries transferPlan out. A route moves in one call. A new trip takes pooled jobs
 * only, so the jobs it is for are detached one by one first, each remembered, and
 * the trip is then created with all of them at once: a refusal in between leaves
 * them in the pool, never an empty trip on the other shift. A route a failed
 * attempt emptied is no longer in the plan, but its remembered jobs still get
 * their trip.
 */
const transferSteps = (
  api: ForceCompleteApi,
  routes: readonly ConnectProto.Route[],
  target: bigint,
  memory: RunMemory,
): Step[] => {
  const sameRoute = (a: { id: bigint }, b: { id: bigint }) => compareIds(a.id, b.id) === 0;
  const remembered = (route: ConnectProto.Route) =>
    (memory.detached ?? []).find((entry) => sameRoute(entry.like, route));
  const remember = (route: ConnectProto.Route, jobId: bigint) => {
    const entry = remembered(route);
    if (entry) entry.jobIds = [...entry.jobIds, jobId];
    else memory.detached = [...(memory.detached ?? []), { like: route, jobIds: [jobId] }];
  };
  const forget = (route: ConnectProto.Route) => {
    memory.detached = (memory.detached ?? []).filter((entry) => !sameRoute(entry.like, route));
  };

  const plan = transferPlan(routes);
  const orphaned = (memory.detached ?? [])
    .filter((entry) => !plan.some((item) => sameRoute(item.route, entry.like)))
    .map((entry): TransferItem => ({ kind: "create", route: entry.like, jobs: [] }));

  return [...plan, ...orphaned].flatMap((item) => {
    if (item.kind === "move") return [() => api.moveRoute(item.route.id, target)];
    return [
      ...item.jobs.map((job) => async () => {
        await api.detachJob(job);
        remember(item.route, job.id);
      }),
      async () => {
        // Read when it runs: by then the detaches above have added their jobs.
        await api.createTrip(item.route, target, remembered(item.route)?.jobIds ?? []);
        forget(item.route);
      },
    ];
  });
};

/**
 * Settles the shift with the existing RPCs, one at a time, then ends it with the
 * reason and explanation. The first refusal stops the run and rejects; what ran stays done, so the
 * caller re-reads the shift and a retry carries on from there.
 */
export const runForceComplete = async ({
  routes,
  resolutions,
  routeResolutions,
  decision: { mode, target, reason, explanation },
  api,
  memory,
  onProgress,
}: {
  routes: readonly ConnectProto.Route[];
  resolutions: Resolutions;
  routeResolutions: RouteResolutions;
  decision: Decision & { reason: ConnectProto.DriverIncidentType };
  api: ForceCompleteApi;
  memory: RunMemory;
  onProgress?: (done: number, total: number) => void;
}) => {
  // After a transfer the routes hold only finished work. Which calls close them is read
  // from the backend, and they join the queue so the progress counts each one.
  const settleLeftovers: Step = async () => {
    const leftovers = unfinishedRoutes(await api.loadRoutes()).flatMap((route) =>
      routeSteps(api, route, () => "unassign", routeResolutionsFor(route.status)[0]),
    );
    steps.splice(steps.indexOf(settleLeftovers) + 1, 0, ...leftovers);
  };
  const steps: Step[] =
    mode === "transfer" && target
      ? [...transferSteps(api, routes, target, memory), settleLeftovers]
      : unfinishedRoutes(routes).flatMap((route) =>
          routeSteps(
            api,
            route,
            (job) => resolutionOf(resolutions, job.id),
            routeResolutionOf(routeResolutions, route),
          ),
        );
  steps.push(() => api.completeShift(reason, explanation.trim()));

  for (let done = 0; done < steps.length; done++) {
    onProgress?.(done, steps.length);
    await steps[done]();
  }
  onProgress?.(steps.length, steps.length);
};
